import { useState, useEffect, useRef } from "react";
import "./chartGuide.css";

function computeMobileBelowPlacement({ rect, bubbleWidth, bubbleHeight, bottomNavbarHeight }) {
  const margin = 12;
  const gap = 24;
  const maxTop = window.innerHeight - bottomNavbarHeight - bubbleHeight - margin;
  const maxLeft = window.innerWidth - bubbleWidth - margin;

  const top = Math.min(Math.max(rect.bottom + gap, margin), Math.max(maxTop, margin));

  const centeredLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
  const left = Math.min(Math.max(centeredLeft, margin), Math.max(maxLeft, margin));

  const ARROW_SIZE = 20;
  const targetCenter = rect.left + rect.width / 2;
  const rawPercent = ((targetCenter - left) / bubbleWidth) * 100;
  const limit = (ARROW_SIZE / bubbleWidth) * 100 + 4;
  const arrowOffset = `${Math.min(Math.max(rawPercent, limit), 100 - limit)}%`;

  return { top, left, arrow: "top", arrowOffset };
}

const WatchlistGuideSteps = ({ setShowGuide, onSelectList }) => {
  const isDesktopView = window.innerWidth > 768;
  const steps = [
    {
      id: 1,
      target: "watchlist-guide-active-list",
      text: isDesktopView
        ? "The list marked Active powers the Watchlist panel on the Trade screen."
        : "The list marked Active powers the asset wheel on the Trade screen.",
    },
    {
      id: 2,
      target: "watchlist-guide-new-button",
      text: isDesktopView
        ? "Click + New to build your own watchlists. Add the stocks, crypto, and forex pairs you want to follow."
        : "Tap + New to build your own watchlists. Add the stocks, crypto, and forex pairs you want to follow.",
    },
    {
      id: 3,
      target: "watchlist-guide-curated-items",
      highlightParent: "watchlist-guide-curated-section",
      text: isDesktopView
        ? "QUSHi Curated Lists are ready made groups for market context. They are view only, but you can make any of them active on the Trade screen."
        : "QUSHi Curated Lists are ready made groups for market context. They are view only, but you can make any of them your active Trade wheel.",
    },
    {
      id: 4,
      target: "watchlist-guide-use-in-trade",
      text: isDesktopView
        ? "Click Use in Trade to make this list active on the Trade screen. Switch anytime as your focus shifts."
        : "Tap Use in Trade to make this list your active Trade wheel. Switch anytime as your focus shifts.",
    },
  ];

  const [stepIndex, setStepIndex] = useState(0);
  const [bubblePosition, setBubblePosition] = useState({ top: -1000, left: -1000 });
  const [arrowPosition, setArrowPosition] = useState("bottom");
  const [arrowOffset, setArrowOffset] = useState("50%");
  const [isPositioned, setIsPositioned] = useState(false);
  const bubbleRef = useRef(null);
  const current = steps[stepIndex];

  const skipToNextStepRef = useRef(null);
  skipToNextStepRef.current = () => {
    if (stepIndex < steps.length - 1) {
      if (stepIndex === 2 && onSelectList) {
        onSelectList("crypto");
      }
      setIsPositioned(false);
      setBubblePosition({ top: -1000, left: -1000 });
      setStepIndex(stepIndex + 1);
      return;
    }
    setShowGuide(false);
  };

  useEffect(() => {
    const prevHighlighted = document.querySelectorAll(".chart-guide-highlight");
    prevHighlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));

    const targetElement = document.getElementById(current.target);
    if (targetElement) {
      targetElement.classList.add("chart-guide-highlight");
    }

    if (current.highlightParent) {
      const parentElement = document.getElementById(current.highlightParent);
      if (parentElement) {
        parentElement.classList.add("chart-guide-highlight");
      }
    }

    return () => {
      const highlightedElement = document.getElementById(current.target);
      if (highlightedElement) {
        highlightedElement.classList.remove("chart-guide-highlight");
      }
      if (current.highlightParent) {
        const parentElement = document.getElementById(current.highlightParent);
        if (parentElement) {
          parentElement.classList.remove("chart-guide-highlight");
        }
      }
    };
  }, [current.target, current.highlightParent]);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const bottomNavbarHeight = isMobile ? 64 : 0;

    const compute = () => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) return;

      const measureElement = current.highlightParent
        ? document.getElementById(current.highlightParent) || targetElement
        : targetElement;
      const rect = measureElement.getBoundingClientRect();
      const bubbleElement = bubbleRef.current;
      const bubbleWidth = bubbleElement?.offsetWidth || (isMobile ? 280 : 320);
      const bubbleHeight = bubbleElement?.offsetHeight || (isMobile ? 200 : 180);

      const { top, left, arrow, arrowOffset } = computeMobileBelowPlacement({
        rect,
        bubbleWidth,
        bubbleHeight,
        bottomNavbarHeight,
      });

      setBubblePosition({ top, left });
      setArrowPosition(arrow);
      setArrowOffset(arrowOffset);
      setIsPositioned(true);
    };

    let settleTimer;
    let retryTimer;
    const maxRetries = isMobile ? 1 : 5;
    const retryDelay = 700;
    const scrollAndPosition = (retryCount = 0) => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) {
        if (retryCount < maxRetries) {
          retryTimer = setTimeout(() => scrollAndPosition(retryCount + 1), retryDelay);
          return;
        }
        if (process.env.NODE_ENV !== "production") {
          console.warn(`[WatchlistGuideSteps] no element #${current.target}, skipping step`);
        }
        skipToNextStepRef.current();
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const comfortablyVisible =
        rect.top > 80 && rect.bottom < window.innerHeight - bottomNavbarHeight - 120;
      if (!comfortablyVisible) {
        targetElement.scrollIntoView({
          behavior: "auto",
          block: "nearest",
          inline: "nearest",
        });
      }

      compute();
      settleTimer = setTimeout(compute, isMobile ? 150 : 220);
    };

    const timer = setTimeout(scrollAndPosition, 60);

    window.addEventListener("resize", compute, { passive: true });
    window.addEventListener("scroll", compute, { passive: true });
    return () => {
      clearTimeout(timer);
      clearTimeout(settleTimer);
      clearTimeout(retryTimer);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute);
    };
  }, [current.target]);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      if (stepIndex === 2 && onSelectList) {
        onSelectList("crypto");
        setTimeout(() => {
          setIsPositioned(false);
          setBubblePosition({ top: -1000, left: -1000 });
          setStepIndex(stepIndex + 1);
        }, 600);
      } else {
        setIsPositioned(false);
        setBubblePosition({ top: -1000, left: -1000 });
        setStepIndex(stepIndex + 1);
      }
      return;
    }
    setShowGuide(false);
  };

  const handleSkip = () => {
    const highlighted = document.querySelectorAll(".chart-guide-highlight");
    highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    setShowGuide(false);
  };

  useEffect(() => {
    return () => {
      const highlighted = document.querySelectorAll(".chart-guide-highlight");
      highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    };
  }, []);

  return (
    <>
      <div className="chart-guide-backdrop" onClick={handleSkip} />

      <div
        ref={bubbleRef}
        className={`chart-guide-bubble dynamic-position arrow-${arrowPosition} step-${stepIndex + 1} watchlist-guide-bubble`}
        style={{
          top: bubblePosition.top,
          left: bubblePosition.left,
          opacity: isPositioned ? 1 : 0,
          "--arrow-offset": arrowOffset,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div className="guide-step-indicator">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <div className="guide-text">{current.text}</div>

        <div className="guide-buttons">
          <button className="next-btn" onClick={handleNext}>
            {stepIndex === steps.length - 1 ? "Finish" : "Next"}
          </button>
          <button className="skip-btn" onClick={handleSkip}>
            Skip
          </button>
        </div>
      </div>
    </>
  );
};

export default WatchlistGuideSteps;
